import * as THREE from "three";
import type { Character, Rig } from "./character";
import type { Controller } from "./deps";
import type { Scene3D } from "./deps";

/**
 * Balance-driven procedural locomotion — nothing baked, no fixed foot cycle, no
 * baked-in world heights (works in any 3-D environment).
 *
 * Feet LOCK where they plant (no slip). A step fires on a speed-scaled clock; the
 * swing foot lands at the CAPTURE POINT — where the body is heading — so stops,
 * turns and slopes produce believable footfalls. The pelvis rides an INVERTED-
 * PENDULUM vault over the support foot. Legs solve with a 3-D two-bone IK, feet
 * finding the ground by probing DOWN from the body (any floor / story / ramp).
 * Lean EMERGES from CoM acceleration (into turns / forward on speed-up).
 *
 * Life layers: coherent noise + L/R asymmetry, spring secondary motion (head +
 * torso lag/overshoot), fatigue drift, gaze drift. Jump blends to an air pose.
 */
// Leg/stance metrics come from the character's RIG (per-body, customizable) —
// see `retune()`. They must be recomputed whenever the body proportions change.
// MIN_STEP 0.12 keeps sprint cadence ~8.3 Hz (7-8 frames/swing at 60fps) — the old
// 0.07 floor ran the whole sprint at a fixed 14.3 Hz and the leg sweep read as vibration.
const STRIDE_EX = 0.42, MIN_STEP = 0.12, MAX_STEP = 0.5;
const STEP_FRAC = 0.22; // step/probe height as a fraction of body height
const LIFT_BASE = 0.05, LIFT_PER_SPEED = 0.01, LIFT_MAX = 0.15;
const LEAN_ACCEL = 0.025, LEAN_FWD_BASE = 0.05, MAX_LEAN = 0.5; // emergent lean (soft-saturated via tanh)
const ARM_GAIN = 1.7, AIR_ARM_SPREAD = 0.4; // airborne: spread arms out for balance (not raise 90°)
// Sprint form: as speed rises the swing leg drives the knee up high (thigh ~90°,
// shin dropping) and the arms bend at the elbow and pump. `run` ramps this in
// smoothly from a jog to a sprint — it is NOT switched on at a threshold.
const RUN_LO = 3.0, RUN_HI = 9.5; // speed window (u/s) over which sprint form blends in
// Swing leg sweeps a full sprint cycle over the swing phase (sagittal, body frame):
// fly out BACK just after toe-off → whip the knee up HIGH in front → REACH to plant.
const DRIVE_BACK = 0.75, KNEE_BACK = 0.5; // trailing leg flung out behind (hip extension)
const DRIVE_HIP = -1.5, DRIVE_KNEE = 1.6; // high-knee at mid-swing (thigh ~90° up, shin dropping)
const DRIVE_REACH = -0.35, KNEE_REACH = 0.2; // reach forward + extend to plant
const ARM_BEND_RUN = -1.55; // elbow flexion at full sprint (~90°)
const JUMP_WINDUP_CROUCH = 0.16, LAND_CROUCH = 0.16; // pelvis dip for wind-up / landing
const HIP_SPLAY = 0.11;
const HEAD_YAW_LIMIT = 1.2;
const JUMP_SPEED = 11.8; // keep in sync with controller.ts (air-tuck normalization)
const TORSO_STIFF = 55, TORSO_DAMP = 10;
const HEAD_STIFF = 90, HEAD_DAMP = 15;
// hip spring critically damped at wn 20 rad/s so the 5 Hz walk vault bob, landing
// dips, and step-plant bounces actually reach the pelvis (130/20 filtered them out).
const HIP_STIFF = 400, HIP_DAMP = 40;
const NOISE = 0.08, ASYM = 0.06, BREATHE = 0.012;

export class Gait {
  private amt = 0;
  private air = 0;
  private time = 0;
  private seed = 0;
  private foot = [new THREE.Vector3(), new THREE.Vector3()];   // 0 = right, 1 = left (ankle targets)
  private plant = [new THREE.Vector3(), new THREE.Vector3()];
  private swingFrom = [new THREE.Vector3(), new THREE.Vector3()];
  private swingTo = [new THREE.Vector3(), new THREE.Vector3()];
  private swing = 0;
  private swingT = 0;
  private ready = false;
  private wasGrounded = true; // landing-edge detector (re-plant + catch step)
  // per-foot yaw: planted toes LOCK their heading (no grinding); the swing foot
  // eases toward the body yaw over its arc. liftYaw = heading at lift-off.
  private footYaw = [0, 0];
  private liftYaw = [0, 0];
  // per-foot ankle pitch (heel-strike/toe-off roll) + ground-slope adaptation
  private anklePitch = [0, 0];
  private slopePitch = [0, 0];
  // idle life: discrete weight shifts + scheduled look-arounds
  private idleT = 0; private shiftSide = 1; private shiftCur = 0; private nextShift = 4; private fidgetStep = false;
  private stepActive = false; // a step is underway → finish it even if `need` drops (no frozen mid-arc foot)
  private nextLook = 8; private lookEvt = 0; private lookEnv = 0; private lookHold = 0; private lookShrug = 0;
  private prevPos = new THREE.Vector3();
  // The root may run inside a uniformly-scaled parent group (giants render at ~37x rig units). The
  // foot IK targets live in root-local rig units, so world-space bone positions must be divided by
  // this to reconcile — otherwise the legs solve against a phantom target far below and hang extended.
  private worldScale = 1;
  private prevVel = new THREE.Vector2();
  private accel = new THREE.Vector2();
  private wob = new THREE.Vector2(); private wobVel = new THREE.Vector2(); // whole-body wobble spring
  // per-bone soft-body jiggle: each jiggly bone's spring orientation + ang. velocity
  private jbones: THREE.Bone[] = [];
  private jsq: THREE.Quaternion[] = []; private jsv: THREE.Vector3[] = [];
  private hipY = 0; private hipYVel = 0;
  private probeBaseY = 0; // TRUE ground height (from the controller) for foot terrain probes
  private prevRootY = 0; private prevGrounded = true; // for the stair-pop pelvis continuity
  private stairDebt = 0; // accumulated pelvis-continuity offset (bounded; decays as the spring re-seats)
  private prevOvr = 0; // last frame's stance override weight (re-plant feet when it releases)
  private crawlPhase = 0; // prone crawl oscillator (distance-driven)
  private bodySlope = 0; // smoothed terrain pitch under a prone body
  private brake = 0; // 0..1 sprint-stop braking (accel opposing velocity at speed)
  private runVis = 0; // visual sprint-form amount: arrives fast, unwinds slow
  private torsoYaw = 0; private torsoYawVel = 0;
  private headYaw = 0; private headYawVel = 0;
  private headPitch = 0; private headPitchVel = 0;
  private fatigue = 0;
  // rig-derived leg/stance metrics (recomputed by retune when the body changes)
  private thigh = 0; private calf = 0; private footH = 0; private footD = 0; private restHipY = 0;
  private legLen = 0; private legEff = 0; private stanceHalf = 0;
  private upperArm = 0; private lowerArm = 0; // for the gun-hold arm IK
  private lastRig: Rig | null = null; // identity of the rig we last tuned to

  constructor(private ch: Character, private world: Scene3D) {
    this.seed = Math.random() * 100;
    this.retune();
  }

  /** Re-read the character's RIG (call after the body proportions change). Also
   *  re-plants the feet so the new stance/leg lengths take effect cleanly. This
   *  is invoked automatically by update() whenever the rig object changes, so a
   *  bare `character.setBody()` (bypassing main's applyBody) can't leave it stale. */
  retune(): void {
    const r = this.ch.rig;
    this.thigh = r.thigh; this.calf = r.calf; this.footH = r.footH; this.footD = r.footD; this.restHipY = r.hipY;
    this.legLen = r.thigh + r.calf; this.legEff = this.legLen * 0.965; this.stanceHalf = r.hipX * 1.02;
    this.hipY = r.hipY;
    this.lastRig = r;
    // jiggly bones = chest/head/arms (NOT legs — they'd fight the foot IK / planting)
    const j = this.ch.joints;
    // arm segment lengths (bone local offsets) for the gun-hold IK
    this.upperArm = Math.abs(j.elbowR.position.y);
    this.lowerArm = Math.abs(j.wristR.position.y);
    this.jbones = [j.spine, j.neck, j.shoulderR, j.shoulderL, j.elbowR, j.elbowL];
    this.jsq = this.jbones.map((b) => b.quaternion.clone());
    this.jsv = this.jbones.map(() => new THREE.Vector3());
    this.ready = false;
  }

  update(dt: number, ctrl: Controller): void {
    if (this.ch.rig !== this.lastRig) this.retune(); // body changed → re-tune before posing
    // Probe feet from the TRUE ground, not root.position.y — the controller renders the body at a
    // stair-smoothed height that dips BELOW the real tread while climbing, and probing from there
    // would start under the tread, miss it, and sink the feet into the steps (worst at a sprint).
    this.probeBaseY = ctrl.groundExactHeight;
    const j = this.ch.joints;
    const root = this.ch.root;
    // Only root's own world matrix is read here (its scale, next line); the ~20 descendant bone
    // matrices are fully recomputed by the updateMatrixWorld(true) below before the leg IK reads them,
    // so computing them here too is wasted — refresh ancestors+self only.
    root.updateWorldMatrix(true, false);
    this.worldScale = _ws.setFromMatrixScale(root.matrixWorld).x || 1;
    const pos = root.position;
    // STAIR-POP pelvis continuity: climbing steps, the root pops a whole riser the instant the
    // footprint crosses a nosing (the root must sit at the honest collision height — offsetting
    // it sinks or floats the model in the steps). Cancel the pop from the pelvis: shift hipY
    // (root-relative) by −Δ so the pelvis WORLD height is continuous, and let the hip spring
    // re-seat it over the next ~0.2s — legs flex then extend, which is what a step-up looks
    // like. Only for grounded→grounded height changes within a step (jumps/landings/teleports
    // keep their own dynamics: landImpact crouch, exact air motion, no cross-map glide).
    if (this.ready && ctrl.grounded && this.prevGrounded) {
      const d = pos.y - this.prevRootY;
      // |d| cap ≈ two risers on a dt-hitch frame (a real teleport moves far more than this).
      if (Math.abs(d) > 1e-5 && Math.abs(d) <= this.ch.height * 0.5) {
        // DEBT-LIMITED absorption, scaled down with speed: at a walk (pops ~2/s, slower than the
        // spring's ~0.2s re-seat) absorb the full riser — smooth stepping; at a sprint (pops faster
        // than the spring recovers) absorb little — unbounded injection accumulates, drives hipY
        // negative (pelvis INTO the steps) and thrashes the spring. Sprint pops blur at speed.
        const runF = clamp((ctrl.speed2d - 3) / 6.5, 0, 1);
        const cap = this.ch.height * 0.22 * (1 - 0.8 * runF);
        const debt2 = clamp(this.stairDebt + d, -cap, cap);
        this.hipY = clamp(this.hipY - (debt2 - this.stairDebt), this.footH * 2, this.restHipY * 1.75);
        this.stairDebt = debt2;
      }
    }
    this.stairDebt *= Math.exp(-6 * dt); // recover roughly in sync with the spring re-seating
    this.prevRootY = pos.y; this.prevGrounded = ctrl.grounded;
    const yaw = root.rotation.y;
    const sdt = Math.min(dt, 0.05);
    this.time += dt;

    const vx = ctrl.velX, vz = ctrl.velZ;
    const speed = Math.hypot(vx, vz);
    const fwdX = Math.sin(yaw), fwdZ = Math.cos(yaw);
    // the character's RIGHT = forward × up. (The old `(cos, -sin)` was the LEFT,
    // so foot 0 = the right leg was being planted on the left → crossed legs.)
    const sideX = -Math.cos(yaw), sideZ = Math.sin(yaw);

    const vfDen = Math.max(dt, 1e-3), vfK = Math.min(1, 6 * dt);
    this.accel.x += ((vx - this.prevVel.x) / vfDen - this.accel.x) * vfK;
    this.accel.y += ((vz - this.prevVel.y) / vfDen - this.accel.y) * vfK;
    this.prevVel.set(vx, vz);

    // braking: accel opposing velocity while fast = a sprint stop → plant ahead,
    // slow the cadence, drop the hips (a readable skid-stop, not a glide to rest).
    const airNow = ctrl.grounded ? 0 : 1;
    const brakeT = clamp(-(this.accel.x * vx + this.accel.y * vz) / (Math.max(speed, 0.5) * 12), 0, 1)
      * clamp((speed - 2.5) / 4, 0, 1) * (1 - airNow);
    this.brake += (brakeT - this.brake) * Math.min(1, 10 * dt);

    // --- wobble: a soft mass that LAGS the body's acceleration (inertia) and
    // springs back with overshoot, so the torso/head don't stop/start cleanly. ---
    const wAmt = this.ch.wobble;
    const WSTIFF = 55, WDAMP = 8, WKICK = 0.42;
    this.wobVel.x += (-WSTIFF * this.wob.x - WDAMP * this.wobVel.x - this.accel.x * WKICK) * sdt;
    this.wobVel.y += (-WSTIFF * this.wob.y - WDAMP * this.wobVel.y - this.accel.y * WKICK) * sdt;
    this.wob.x += this.wobVel.x * sdt;
    this.wob.y += this.wobVel.y * sdt;
    // project the lag into the body frame + baked coherent noise (never perfectly still)
    const wFwd = (this.wob.x * fwdX + this.wob.y * fwdZ) * wAmt;
    const wSide = (this.wob.x * sideX + this.wob.y * sideZ) * wAmt;
    const wMove = 0.25 + 0.75 * clamp(speed / 3, 0, 1); // calm at idle, full in motion
    const wN1 = 0.05 * wAmt * wMove * wobble(this.time * 1.9 + this.seed);
    const wN2 = 0.05 * wAmt * wMove * wobble(this.time * 2.5 + this.seed + 4.2);

    this.amt += (Math.min(1, speed / 1.4) - this.amt) * Math.min(1, 8 * dt);
    this.air += ((ctrl.grounded ? 0 : 1) - this.air) * Math.min(1, 12 * dt);
    const amt = this.amt, air = this.air, idle = 1 - amt;
    this.fatigue = clamp(this.fatigue + (speed / 11 - 0.35) * 0.06 * dt, 0, 1);

    // shorten steps while turning hard so a wrong-side foot corrects fast (less time crossed).
    // NOT while aiming: the body snaps to the cursor at rate 16, which spikes turnRate and would
    // fire frantic mini-steps even standing still — and the anti-cross clamps already suffice.
    const turnUrgency = ctrl.aiming ? 0 : clamp(Math.abs(ctrl.turnRate) / 2.5, 0, 1);
    // multipliers INSIDE the clamp so a hard turn can never produce sub-MIN_STEP strobe
    // steps; speed floor 0.8 lets near-idle correction steps take a calm ~0.5 s.
    const stepDur = clamp(
      (STRIDE_EX / Math.max(speed, 0.8)) * (1 + 0.12 * this.fatigue) * lerp(1, 0.55, turnUrgency) * (1 + 0.35 * this.brake),
      MIN_STEP, MAX_STEP,
    );
    const lift = clamp(LIFT_BASE + speed * LIFT_PER_SPEED, LIFT_BASE, LIFT_MAX);
    // sprint form arrives WITH the acceleration (rate 9) but unwinds slower (~0.45 s)
    // after Shift releases — the body visibly relaxes out of high-knees/elbow-pump
    // while the speed tween is still bleeding off.
    const runT = smooth(clamp((speed - RUN_LO) / (RUN_HI - RUN_LO), 0, 1));
    this.runVis += (runT - this.runVis) * Math.min(1, (runT > this.runVis ? 9 : 2.2) * dt);
    const run = this.runVis;

    // ---- stances (eased 0..1 from the controller): crouch modulates the normal gait;
    // prone/slide/dive REPLACE the legs+stepping with direct poses (ovr weight) and lay
    // the body down via a pelvis pitch (flat weight). Everything crossfades. ----
    const crouch = ctrl.crouchAmt, prone = ctrl.proneAmt, slide = ctrl.slideAmt, dive = ctrl.diveAmt;
    const ovr = Math.max(prone, slide, dive); // stepping/leg-IK override weight
    const flat = Math.max(prone, dive); // body-horizontal weight
    // prone crawl oscillator: distance-driven, alternating arm reach / leg push
    if (prone > 0.3) this.crawlPhase += speed * dt * 5.5;
    // terrain pitch under a lying body: probe chest-ward and feet-ward, align the body
    // to the slope so prone works on ramps/stairs, not just flat ground.
    if (flat > 0.01) {
      const span = this.ch.height * 0.42;
      const gyF = this.groundY(pos.x + fwdX * span, pos.z + fwdZ * span);
      const gyB = this.groundY(pos.x - fwdX * span * 0.5, pos.z - fwdZ * span * 0.5);
      const slopeT = clamp(Math.atan2(gyF - gyB, span * 1.5), -0.7, 0.7);
      this.bodySlope += (slopeT - this.bodySlope) * Math.min(1, 8 * dt);
    } else this.bodySlope *= Math.max(0, 1 - 8 * dt);

    // (re)initialize feet on first run or after a teleport
    if (!this.ready || this.prevPos.distanceTo(pos) > 2) {
      for (let i = 0; i < 2; i++) {
        const s = i === 0 ? 1 : -1;
        const fx = pos.x + sideX * s * this.stanceHalf, fz = pos.z + sideZ * s * this.stanceHalf;
        this.foot[i].set(fx, this.groundY(fx, fz) + this.footH, fz);
        this.plant[i].copy(this.foot[i]); this.swingFrom[i].copy(this.foot[i]); this.swingTo[i].copy(this.foot[i]);
        this.footYaw[i] = yaw; this.liftYaw[i] = yaw;
      }
      this.swing = 0; this.swingT = 0; this.ready = true;
      // torsoYaw is a WORLD yaw (spine.y = shortestAngle(yaw, torsoYaw)); headYaw is
      // NECK-LOCAL — seeding it with the world yaw pinned the head sideways whenever a
      // body-slider re-init fired while the character faced off-axis. Start neutral.
      this.torsoYaw = yaw;
      this.headYaw = 0; this.headYawVel = 0; this.headPitch = 0; this.headPitchVel = 0;
      this.retarget(0, pos, vx, vz, sideX, sideZ, stepDur);
    }
    this.prevPos.copy(pos);

    // ---- landing: RE-PLANT both feet under the body (the airborne IK targets are
    // stale where the jump began — without this the legs rubber-band on touchdown;
    // no visual pop: airborne legs render the air-pose slerp, not these targets). ----
    const landedNow = ctrl.grounded && !this.wasGrounded;
    this.wasGrounded = ctrl.grounded;
    if (landedNow) {
      for (let i = 0; i < 2; i++) {
        const s = i === 0 ? 1 : -1;
        const fx = pos.x + sideX * s * this.stanceHalf + vx * 0.06; // velocity lead
        const fz = pos.z + sideZ * s * this.stanceHalf + vz * 0.06;
        this.foot[i].set(fx, this.groundY(fx, fz) + this.footH, fz);
        this.plant[i].copy(this.foot[i]); this.swingFrom[i].copy(this.foot[i]); this.swingTo[i].copy(this.foot[i]);
        this.footYaw[i] = yaw; this.liftYaw[i] = yaw;
      }
      this.swingT = 0;
      this.retarget(this.swing, pos, vx, vz, sideX, sideZ, stepDur);
      // hard + moving → a CATCH step braced well ahead of the CoM (stagger absorb);
      // strict alternation then supplies the settling step for free.
      if (ctrl.landImpact > 0.25 && speed > 0.5) {
        this.swing = ctrl.hopParity; // alternates per hop
        this.swingFrom[this.swing].copy(this.foot[this.swing]);
        this.retarget(this.swing, pos, vx, vz, sideX, sideZ, stepDur);
        this.swingTo[this.swing].x += fwdX * 0.3 * ctrl.landImpact;
        this.swingTo[this.swing].z += fwdZ * 0.3 * ctrl.landImpact;
        this.swingTo[this.swing].y = this.groundY(this.swingTo[this.swing].x, this.swingTo[this.swing].z) + this.footH;
      }
    }

    // leaving a leg-override stance (prone/slide/dive → upright): the stored plants are
    // stale from where the body lay down — re-plant both feet under the body so the
    // returning IK doesn't rubber-band the legs to old positions.
    if (this.prevOvr >= 0.5 && ovr < 0.5) {
      for (let i = 0; i < 2; i++) {
        const s = i === 0 ? 1 : -1;
        const fx = pos.x + sideX * s * this.stanceHalf, fz = pos.z + sideZ * s * this.stanceHalf;
        this.foot[i].set(fx, this.groundY(fx, fz) + this.footH, fz);
        this.plant[i].copy(this.foot[i]); this.swingFrom[i].copy(this.foot[i]); this.swingTo[i].copy(this.foot[i]);
        this.footYaw[i] = yaw; this.liftYaw[i] = yaw;
      }
      this.swingT = 0;
    }
    this.prevOvr = ovr;

    // ---- stepping (capture point), only while grounded ----
    if (air < 0.5) {
      // turn-in-place: a big twist between the planted toes and the body fires a
      // repositioning step even at zero speed (the classic standing pivot-shuffle).
      const twist = Math.max(Math.abs(shortestAngle(this.footYaw[0], yaw)), Math.abs(shortestAngle(this.footYaw[1], yaw)));
      const need = ovr < 0.5 && (speed > 0.25 || twist > 0.6 || this.fidgetStep
        || this.offIdeal(0, pos, sideX, sideZ) > 0.08 || this.offIdeal(1, pos, sideX, sideZ) > 0.08);
      // a step already underway must FINISH (plant) even if `need` drops mid-arc —
      // else creeping to a stop froze the swing foot hovering. stepActive latches on
      // `need` and clears only once the in-flight step has planted with no new need.
      if (need) this.stepActive = true;
      if (this.stepActive) this.swingT += dt / stepDur;
      let guard = 0;
      while (this.swingT >= 1 && guard++ < 4) {
        this.swingT -= 1;
        if (!need) this.stepActive = false; // completed the last step; hold this stance
        this.fidgetStep = false; // the idle weight-shift step (if any) has completed
        this.plant[this.swing].copy(this.swingTo[this.swing]);
        this.foot[this.swing].copy(this.swingTo[this.swing]);
        this.footYaw[this.swing] = yaw; // planted toes lock this heading (no grinding)
        // step-plant impact: a downward velocity kick the stiff hip spring turns
        // into a 1-3 cm dip-and-recover per footfall, plus a lagged head-nod beat.
        this.hipYVel -= (0.15 + 0.45 * run) * amt;
        this.wobVel.x -= fwdX * 0.3 * run;
        this.wobVel.y -= fwdZ * 0.3 * run;
        // strict alternation: the foot that just planted is on its (clamped) correct
        // side, so the OTHER foot is exactly the one to swing next — including to
        // un-cross itself after a turn. (offIdeal-based selection limped: the just-
        // planted foot sits a half-stride ahead of its ideal, so it isn't "near ideal".)
        this.swing = 1 - this.swing;
        this.retarget(this.swing, pos, vx, vz, sideX, sideZ, stepDur);
        this.swingFrom[this.swing].copy(this.foot[this.swing]);
        this.liftYaw[this.swing] = this.footYaw[this.swing]; // heading at lift-off
      }
      const sw = this.swing, e = smooth(this.swingT);
      // fidget/idle steps still arc (amt≈0 used to make them SLIDE flat)
      const liftAmt = Math.max(amt, 0.3);
      this.foot[sw].lerpVectors(this.swingFrom[sw], this.swingTo[sw], e);
      this.foot[sw].y += lift * Math.sin(Math.PI * this.swingT) * liftAmt;
      this.foot[1 - sw].copy(this.plant[1 - sw]);
      // swing foot's toes ease from their lift-off heading to the body's over the arc
      this.footYaw[sw] = this.liftYaw[sw] + shortestAngle(this.liftYaw[sw], yaw) * smooth(clamp(this.swingT, 0, 1));
      // keep the swinging foot on its own side of the planted foot DURING the swing too,
      // so it arcs around rather than straight through the other leg on a hard turn.
      const ss = sw === 0 ? 1 : -1, po = this.plant[1 - sw];
      const swSep = (this.foot[sw].x - po.x) * sideX + (this.foot[sw].z - po.z) * sideZ;
      const swMin = this.stanceHalf * 0.6;
      if (ss > 0 ? swSep < swMin : swSep > -swMin) {
        const d = ss * swMin - swSep;
        this.foot[sw].x += sideX * d; this.foot[sw].z += sideZ * d;
        // re-ground at the shifted x/z (else the foot floats/sinks on a slope) + re-apply the arc lift
        this.foot[sw].y = this.groundY(this.foot[sw].x, this.foot[sw].z) + this.footH + lift * Math.sin(Math.PI * this.swingT) * liftAmt;
      }
    }

    // ---- idle life: discrete weight shifts (re-plant a foot, lean onto the other)
    // and scheduled look-arounds. Dissolves instantly on any input (idle-gated). ----
    if (amt < 0.1 && air < 0.1 && ovr < 0.3 && crouch < 0.7) {
      this.idleT += dt;
      if (this.idleT > this.nextShift) {
        this.shiftSide *= -1;
        this.nextShift = this.idleT + 3 + 4 * Math.random();
        this.swing = this.shiftSide > 0 ? 0 : 1; // re-plant the unloading foot
        this.swingT = 0;
        this.fidgetStep = true; // keep the step clock running until this completes
        this.swingFrom[this.swing].copy(this.foot[this.swing]);
        this.retarget(this.swing, pos, vx, vz, sideX, sideZ, stepDur);
      }
      if (this.idleT > this.nextLook && !ctrl.aiming) { // no idle glances while aiming down a gun
        this.nextLook = this.idleT + 6 + 4 * Math.random();
        this.lookEvt = (Math.random() < 0.5 ? -1 : 1) * (0.5 + 0.6 * Math.random());
        this.lookHold = 1.2;
        this.lookShrug = Math.random() < 0.3 ? 1 : 0;
      }
    } else {
      // leaving idle: re-base the fidget/look clocks to fresh SHORT deltas, else the
      // stale absolute nextShift/nextLook (grown over a long idle) stall idle life for
      // tens of seconds after the next idle→move→idle cycle.
      this.idleT = 0;
      this.nextShift = 3 + 4 * Math.random();
      this.nextLook = 6 + 4 * Math.random();
    }
    // asymmetric: lazy ~0.8 s ease INTO a shift while idle, fast dissolve (8/s) the
    // moment movement starts — no lopsided pelvis carried into the first strides.
    const shiftTgt = this.shiftSide * idle * (1 - air) * (this.idleT > 0 ? 1 : 0);
    const shiftRate = (amt > 0.1 || air > 0.1 || Math.abs(shiftTgt) < Math.abs(this.shiftCur)) ? 8 : 1.2;
    this.shiftCur += (shiftTgt - this.shiftCur) * Math.min(1, shiftRate * dt);
    if (this.lookHold > 0) { this.lookHold -= dt; this.lookEnv += (1 - this.lookEnv) * Math.min(1, 5 * dt); }
    else this.lookEnv *= Math.max(0, 1 - 3 * dt);

    // hip-lead signals (feet fore/aft of the body) — reused by the pelvis wring + arms
    const fFwdR = (this.foot[0].x - pos.x) * fwdX + (this.foot[0].z - pos.z) * fwdZ;
    const fFwdL = (this.foot[1].x - pos.x) * fwdX + (this.foot[1].z - pos.z) * fwdZ;

    // ---- pelvis vault: pendulum over the feet MIDPOINT (which straddles the CoM,
    // so the hip stays up even when short legs can't keep a foot under the body) ----
    _mid.addVectors(this.foot[0], this.foot[1]).multiplyScalar(0.5);
    const dh = Math.min(Math.hypot(pos.x - _mid.x, pos.z - _mid.z), this.legEff * 0.9);
    const vaultY = _mid.y + Math.sqrt(this.legEff * this.legEff - dh * dh) - pos.y;
    const breathe = BREATHE * Math.sin(this.time * 1.7 + this.seed) * idle;
    // posture stiffness: pull partway back to standing height so fast movement
    // reads as a lean-and-run rather than a deep squat (legs stretch a touch).
    // stiffer/upright at a walk; looser at speed so the sprint drops into a low,
    // wide-strided, forward-leaning run (like a real sprinter).
    const upright = lerp(vaultY, this.restHipY, lerp(0.55, 0.2, clamp((speed - 3) / 8, 0, 1)));
    // ^0.7 shaping: half-height drops still produce a readable absorb dip. Gated by
    // (1-air) so a bhop re-jump doesn't drag the landing crouch up into flight.
    // max(0,·) before pow: a negative landImpact would make pow(x, 0.7) NaN, which poisons hipY
    // → every bone matrix → the model vanishes permanently (belt-and-suspenders with the
    // controller's own clamp — this line must never be able to mint a NaN).
    const preload = JUMP_WINDUP_CROUCH * ctrl.jumpCrouch + LAND_CROUCH * Math.pow(Math.max(0, ctrl.landImpact), 0.7) * (1 - air);
    let targetHipY = lerp(upright, this.restHipY, air) + breathe - 0.02 * this.fatigue - preload - 0.06 * this.brake;
    // stance pelvis heights: crouch squats partway, a slide skims low, prone lies at the
    // floor, a dive holds the pelvis mid-air while the body levels out. The one hip spring
    // eases every transition (stand↔crouch↔prone↔slide↔dive) for free.
    targetHipY *= 1 - 0.42 * crouch;
    targetHipY = lerp(targetHipY, this.footH * 2 + 0.02, prone);
    targetHipY = lerp(targetHipY, this.restHipY * 0.34, slide);
    targetHipY = lerp(targetHipY, this.restHipY * 0.7, dive);
    this.hipYVel += ((targetHipY - this.hipY) * HIP_STIFF - this.hipYVel * HIP_DAMP) * sdt;
    this.hipY += this.hipYVel * sdt;
    // hard floor: a big stair-pop displacement can make the spring overshoot the pelvis down
    // to (or below) the root — physically the hips can't reach the floor; stop the dive there.
    if (this.hipY < this.footH * 2) { this.hipY = this.footH * 2; if (this.hipYVel < 0) this.hipYVel = 0; }
    // self-heal: hipY drives the pelvis, and the pelvis drives EVERY bone — one NaN here and
    // the model is invisible for the rest of the session. Reset instead of propagating.
    if (!isFinite(this.hipY) || !isFinite(this.hipYVel)) { this.hipY = this.restHipY; this.hipYVel = 0; }
    // lateral sway onto the stance foot + Trendelenburg list (swing-side hip drops)
    // + transverse wring (forward foot's hip leads) + the idle weight shift. Root-
    // local +X = the character's LEFT. Applied before the leg IK re-solves, so the
    // legs plant from the displaced hips.
    const stepS = Math.sin(Math.PI * clamp(this.swingT, 0, 1));
    const gaitFade = amt * (1 - 0.6 * run) * (1 - air);
    const swayX = (this.swing === 1 ? -1 : 1) * 0.22 * this.stanceHalf * stepS * gaitFade; // toward the planted foot
    const pelvList = (this.swing === 1 ? 1 : -1) * 0.07 * stepS * gaitFade;
    const pelvYaw = clamp(0.3 * (fFwdR - fFwdL), -0.22, 0.22) * (1 - air) * (ctrl.aiming ? 0.4 : 1);
    const idleShiftX = 0.045 * this.shiftCur, idleRoll = -0.10 * this.shiftCur;
    // body-flat pitch: prone lays the torso level with the TERRAIN (bodySlope from the
    // fore/aft probes — ramps and stair runs read correctly); a dive is the airborne
    // superman (slightly shy of horizontal); a slide reclines the pelvis back a touch.
    // Pitching the PELVIS carries the whole body: torso sweeps down-forward, and the
    // legs' rest direction (pelvis-down) becomes world-backward — lying at full length.
    const bodyPitch = prone * clamp(Math.PI / 2 - 0.14 - this.bodySlope, 0.8, 1.85)
      + dive * 1.25 - slide * 0.3;
    j.pelvis.position.set(swayX + idleShiftX, this.hipY + 0.012 * Math.abs(this.shiftCur), 0);
    j.pelvis.rotation.set(bodyPitch, pelvYaw * (1 - flat), (pelvList + idleRoll) * (1 - flat));

    // ---- emergent lean from CoM acceleration ----
    // tanh soft saturation: slope ~1 in the normal range, compresses sprint-start
    // spikes instead of pinning at a hard rail (the wobble then overshoots around a
    // moving lean, not around a wall). Landing folds the torso forward one beat.
    const aFwd = this.accel.x * fwdX + this.accel.y * fwdZ;
    const aSide = this.accel.x * sideX + this.accel.y * sideZ;
    const rawP = aFwd * LEAN_ACCEL + LEAN_FWD_BASE * amt + speed * 0.016 + 0.35 * ctrl.landImpact * (1 - air);
    const leanPitch = MAX_LEAN * Math.tanh(rawP / MAX_LEAN);
    // lean INTO the lateral acceleration: aSide>0 (accel toward the char's right)
    // → spine.z>0 (torso tips toward the char's right).
    const leanRoll = MAX_LEAN * Math.tanh((aSide * LEAN_ACCEL) / MAX_LEAN);

    // ---- air leg pose: symmetric tuck for a single jump, asymmetric SKIP during
    // a bhop chain. The skip now borrows the SPRINT split — the lead leg drives
    // the knee up HIGH in front, the other flings out BACK — so a bhop reads as a
    // running bound, not a shallow tuck. Alternates via hopParity each hop. ----
    const tuck = 1 - clamp(Math.abs(ctrl.vy) / JUMP_SPEED, 0, 1);
    const symHip = -0.2 - 0.6 * tuck, symKnee = 0.3 + 1.1 * tuck;
    const skip = clamp(ctrl.bhop, 0, 1);
    const rLead = ctrl.hopParity === 0; // right leg leads (high-knee) this hop
    const airHipR = lerp(symHip, rLead ? DRIVE_HIP : DRIVE_BACK, skip), airKneeR = lerp(symKnee, rLead ? DRIVE_KNEE : KNEE_BACK, skip);
    const airHipL = lerp(symHip, rLead ? DRIVE_BACK : DRIVE_HIP, skip), airKneeL = lerp(symKnee, rLead ? KNEE_BACK : DRIVE_KNEE, skip);

    // ---- legs: 3-D IK to the live foot positions, blending to the air pose ----
    // High-knee sprint drive: only the SWING leg, peaking mid-swing, scaled by
    // `run`. The stance (planted) leg stays pure IK so ground contact is exact.
    root.updateMatrixWorld(true);
    // swing-phase pose sweep: BACK (fly out behind) → HIGH-KNEE (front) → REACH.
    // Broad envelope holds it active across the swing, easing only at the very
    // ends so it hands back to the planted-leg IK cleanly.
    const p = clamp(this.swingT, 0, 1);
    const env = smooth(clamp(p / 0.18, 0, 1)) * smooth(clamp((1 - p) / 0.18, 0, 1));
    const drive = run * env * (1 - air);
    let driveHipX: number, driveKneeX: number;
    if (p < 0.3) { driveHipX = DRIVE_BACK; driveKneeX = KNEE_BACK; }
    else if (p < 0.55) { const u = smooth((p - 0.3) / 0.25); driveHipX = lerp(DRIVE_BACK, DRIVE_HIP, u); driveKneeX = lerp(KNEE_BACK, DRIVE_KNEE, u); }
    else { const u = smooth((p - 0.55) / 0.45); driveHipX = lerp(DRIVE_HIP, DRIVE_REACH, u); driveKneeX = lerp(DRIVE_KNEE, KNEE_REACH, u); }
    // foot roll (heel-strike / toe-off) + ground-slope adaptation, per foot:
    // the swing foot dorsiflexes (heel-first contact) late in its arc; the planted
    // foot plantarflexes (heel peels, toe-off push) through late stance — the swing
    // clock IS the planted foot's late-stance clock. Slope pitch from fore/aft probes.
    for (let i = 0; i < 2; i++) {
      const ffx = Math.sin(this.footYaw[i]), ffz = Math.cos(this.footYaw[i]);
      const fy = this.foot[i].y - this.footH; // this foot's own ground height
      const gyF = this.groundY(this.foot[i].x + ffx * 0.09, this.foot[i].z + ffz * 0.09);
      const gyB = this.groundY(this.foot[i].x - ffx * 0.09, this.foot[i].z - ffz * 0.09);
      // clamp each probe to ±footD of the foot's ground: a probe that straddles a
      // curb/ledge hits a surface far below (a discontinuity, NOT a slope) — unclamped
      // it twisted the ankle up to ~90°. Clamp the eased result too so ramps still read.
      const dF = clamp(gyF - fy, -this.footD, this.footD), dB = clamp(gyB - fy, -this.footD, this.footD);
      const slopeTgt = clamp(Math.atan2(dF - dB, 0.18), -0.4, 0.4);
      this.slopePitch[i] += (slopeTgt - this.slopePitch[i]) * Math.min(1, 10 * dt);
      const gaitTheta = i === this.swing
        ? -0.28 * smooth(clamp((this.swingT - 0.7) / 0.3, 0, 1)) * amt * (1 - 0.5 * run) // heel-strike (toe up)
        : (0.35 + 0.3 * run) * smooth(clamp((this.swingT - 0.55) / 0.45, 0, 1)) * amt;   // toe-off (heel peels)
      this.anklePitch[i] += ((this.slopePitch[i] + gaitTheta) * (1 - air) - this.anklePitch[i]) * Math.min(1, 14 * dt);
    }
    // stance leg poses (pelvis-LOCAL — the pelvis pitch already lays the body down, so
    // prone's near-zero hip angles = legs stretched along the ground behind). They ride
    // the same clean-sagittal slerp as the air tuck; the tuck itself is suppressed under
    // any override so a dive flies stretched, not tucked. Crawl alternates via crawlPhase.
    const cw = Math.sin(this.crawlPhase);
    const wAirOnly = air * (1 - ovr);
    const den = Math.max(wAirOnly + prone + slide + dive, 1e-4);
    const ovrW = clamp(wAirOnly + ovr, 0, 1); // air tuck when airborne-only, stance pose under an override
    const oHipR = (wAirOnly * airHipR + prone * (-0.06 + 0.28 * cw) + slide * -1.5 + dive * 0.12) / den;
    const oKneeR = (wAirOnly * airKneeR + prone * (0.18 + 0.3 * Math.max(0, cw)) + slide * 0.25 + dive * 0.12) / den;
    const oHipL = (wAirOnly * airHipL + prone * (-0.06 - 0.28 * cw) + slide * -1.25 + dive * 0.12) / den;
    const oKneeL = (wAirOnly * airKneeL + prone * (0.18 + 0.3 * Math.max(0, -cw)) + slide * 0.75 + dive * 0.12) / den;
    this.solveLeg(j.hipR, j.kneeR, j.ankleR, this.foot[0], fwdX, fwdZ, +1, ovrW, oHipR, oKneeR, this.swing === 0 ? drive * (1 - ovr) : 0, driveHipX, driveKneeX, this.footYaw[0], this.anklePitch[0]);
    this.solveLeg(j.hipL, j.kneeL, j.ankleL, this.foot[1], fwdX, fwdZ, -1, ovrW, oHipL, oKneeL, this.swing === 1 ? drive * (1 - ovr) : 0, driveHipX, driveKneeX, this.footYaw[1], this.anklePitch[1]);

    // ---- arms: counter-swing coupled to the actual foot positions. At speed the
    // swing widens and the elbows fold toward ~90° and pump (sprinter's arms). ----
    // asymmetric gain: boost the FORWARD swing, trim the back-swing — the raw
    // foot-coupled signal is stride-asymmetric the wrong way round (planted foot
    // trails a full stride → 79° back-swing vs 39° forward, inverted sprinter form).
    const gR = fFwdR > 0 ? ARM_GAIN * (1 + 0.45 * run) : ARM_GAIN * (1 - 0.4 * run);
    const gL = fFwdL > 0 ? ARM_GAIN * (1 + 0.45 * run) : ARM_GAIN * (1 - 0.4 * run);
    const armR = clamp(fFwdR * gR * (1 + ASYM + NOISE * wobble(this.time * 0.9 + this.seed)), -1.0, 1.3);
    const armL = clamp(fFwdL * gL * (1 - ASYM + NOISE * wobble(this.time * 0.9 + this.seed + 3.1)), -1.0, 1.3);
    // elbow (grounded): slight bend at a walk → ~90° flexed + pumping at a sprint.
    const elbowR = lerp(-0.15 - 0.2 * Math.max(0, armR), ARM_BEND_RUN + 0.3 * armR, run);
    const elbowL = lerp(-0.15 - 0.2 * Math.max(0, armL), ARM_BEND_RUN + 0.3 * armL, run);
    // airborne arms: a single jump SPREADS them straight for balance; a bhop SKIP
    // pumps them contralateral to the lead leg with bent elbows (run-through-air).
    const skipArmR = rLead ? 0.8 : -0.5, skipArmL = rLead ? -0.5 : 0.8; // lead-side arm swings back
    const airPitchR = lerp(-0.12, skipArmR, skip), airPitchL = lerp(-0.12, skipArmL, skip);
    const airRollR = lerp(-AIR_ARM_SPREAD, -0.1, skip), airRollL = lerp(AIR_ARM_SPREAD, 0.1, skip);
    const airElbow = lerp(-0.25, ARM_BEND_RUN, skip); // straight balance → bent pump
    j.shoulderR.rotation.set(lerp(armR, airPitchR, air), 0, lerp(0, airRollR, air));
    j.shoulderL.rotation.set(lerp(armL, airPitchL, air), 0, lerp(0, airRollL, air));
    j.elbowR.rotation.x = lerp(elbowR, airElbow, air);
    j.elbowL.rotation.x = lerp(elbowL, airElbow, air);
    // landing absorb: the arms flare out/forward one beat after touchdown (sin of
    // the decaying impact peaks ~0.17 s in), elbows soften — the body catches itself.
    const rec = Math.sin(Math.PI * clamp(ctrl.landImpact, 0, 1)) * (1 - air);
    if (rec > 0.001) {
      j.shoulderR.rotation.z -= 0.45 * rec;
      j.shoulderL.rotation.z += 0.45 * rec;
      j.shoulderR.rotation.x += 0.25 * rec;
      j.shoulderL.rotation.x += 0.25 * rec;
      j.elbowR.rotation.x = lerp(j.elbowR.rotation.x, -0.2, 0.5 * rec);
      j.elbowL.rotation.x = lerp(j.elbowL.rotation.x, -0.2, 0.5 * rec);
    }

    // ---- torso: spring toward the legs' heading (lag + overshoot) + lean ----
    this.torsoYawVel += (shortestAngle(this.torsoYaw, yaw) * TORSO_STIFF - this.torsoYawVel * TORSO_DAMP) * sdt;
    this.torsoYaw += this.torsoYawVel * sdt;
    if (ctrl.aiming) {
      // aim the chest up/down so the (spine-mounted) gun barrel points at the cursor.
      // FULLY cancel the pelvis wring/list so the gun stays level and on target.
      // Recoil kicks the chest back a touch; a melee swing twists the torso into it.
      const swTwist = this.ch.swing > 0 ? 0.35 * Math.sin((1 - this.ch.swing) * Math.PI) : 0;
      j.spine.rotation.set(
        -ctrl.aimPitch - this.ch.gunKick * this.ch.gunKick * 0.05,
        -pelvYaw + (this.ch.weaponDom === "right" ? -swTwist : swTwist),
        -pelvList,
      );
    } else {
      // chest counter-wind: -1.0·pelvYaw cancels the pelvis, the extra -0.3 is the
      // visible counter-twist; -0.6·pelvList is the balancing counter-roll.
      j.spine.rotation.set(
        lerp(leanPitch, -0.08, air) + 0.02 * wobble(this.time * 0.8 + this.seed) + wFwd * 0.8 + wN1,
        shortestAngle(yaw, this.torsoYaw) - 1.3 * pelvYaw,
        lerp(leanRoll, 0, air * 0.5) - wSide * 0.8 + wN2 - 0.6 * pelvList + 0.05 * this.shiftCur,
      );
    }

    // ---- head: look AT the camera when it's in front (face the viewer); ease
    // back to forward when it swings behind (can't crane the head around). ----
    const toCam = shortestAngle(this.torsoYaw, ctrl.lookYaw); // angle to the camera
    const front = 1 - clamp((Math.abs(toCam) - HEAD_YAW_LIMIT) / (Math.PI - HEAD_YAW_LIMIT), 0, 1);
    const gaze = 0.08 * wobble(this.time * 0.3 + this.seed) * idle;
    // head LEADS a heading change (anticipation): headingErr is the yaw still to
    // turn — the head (stiff spring) arrives first, torso trails. Off while aiming.
    const lead = clamp(ctrl.headingErr * 0.6, -0.7, 0.7) * amt * (1 - air) * (ctrl.aiming ? 0 : 1);
    const glance = ctrl.aiming ? 0 : 1; // idle glances die instantly when a gun comes up
    const tgtY = clamp(toCam, -HEAD_YAW_LIMIT, HEAD_YAW_LIMIT) * front + gaze + lead + this.lookEvt * this.lookEnv * idle * glance;
    // while aiming, the neck must track the rate-16 body snap — stiffen way up so
    // aim flicks don't leave the head overshooting in world space.
    const hs = ctrl.aiming ? 240 : HEAD_STIFF, hd = ctrl.aiming ? 31 : HEAD_DAMP;
    this.headYawVel += ((tgtY - this.headYaw) * hs - this.headYawVel * hd) * sdt;
    this.headYaw += this.headYawVel * sdt;
    // pitch UP toward an elevated camera / down toward a low one (−lookPitch: +neck.x looks down).
    // While aiming: the spine already pitches to -aimPitch, so the neck only adds a
    // little (0.3), and skips the lean compensation the aiming spine never applies.
    // Landing dips the gaze (0.4·landImpact) for one beat.
    const pitchF = ctrl.aiming ? 0.3 : 0.75;
    const leanComp = ctrl.aiming ? 0 : leanPitch * 0.5;
    const tgtP = lerp(
      clamp(-ctrl.lookPitch * pitchF, -0.6, 0.6) * front - leanComp + 0.08 * this.fatigue
        + 0.4 * ctrl.landImpact * (1 - air) + 0.15 * this.lookEnv * idle * glance * Math.sign(this.lookEvt),
      0.05, air,
    );
    this.headPitchVel += ((tgtP - this.headPitch) * hs - this.headPitchVel * hd) * sdt;
    this.headPitch += this.headPitchVel * sdt;
    // the head bobbles a touch more than the torso (secondary mass on the neck)
    j.neck.rotation.set(this.headPitch + wFwd * 1.3 + wN1 * 1.3, this.headYaw, -leanRoll * 0.4 - wSide * 1.3 + wN2 * 1.3);
    // some idle look-arounds carry a small head-cock + shoulder shrug
    if (this.lookShrug && glance) {
      j.neck.rotation.z += 0.08 * this.lookEnv * idle;
      j.shoulderR.rotation.z -= 0.1 * this.lookEnv * idle;
    }

    // ---- joint sanity limits: every warp joint + neck, but NOT the ball-jointed
    // hips/shoulders (those stay free for full-range mobility). ----
    this.clampJoints();

    // ---- stance body language (post-clamp: these are deliberate poses, and the neck
    // needs range past its idle sanity cone to look forward from a lying body). ----
    if (crouch > 0.01) {
      j.spine.rotation.x += 0.28 * crouch; // hunch
      j.neck.rotation.x -= 0.22 * crouch; // eyes stay level
    }
    if (flat > 0.01) {
      // crane the head up so a lying body looks forward, not at the dirt
      j.neck.rotation.x -= (prone * bodyPitch * 0.62 + dive * 0.85);
      j.spine.rotation.x += prone * (-0.12) + dive * 0.1; // slight back-arch prone, tuck-arch diving
    }
    if (slide > 0.01) {
      j.spine.rotation.x -= 0.5 * slide; // recline into the slide
      j.neck.rotation.x += 0.3 * slide; // head stays on the horizon
    }
    // arms (skipped while a gun/grenade/jab owns them — those solvers run later and win):
    if (!ctrl.aiming) {
      if (slide > 0.3) {
        const w = smooth(clamp((slide - 0.3) / 0.5, 0, 1));
        _q1.copy(j.shoulderR.quaternion); j.shoulderR.rotation.set(0.8, 0, -0.55); j.shoulderR.quaternion.slerp(_q1, 1 - w); // trailing arm braces back
        _q1.copy(j.elbowR.quaternion); j.elbowR.rotation.set(-0.35, 0, 0); j.elbowR.quaternion.slerp(_q1, 1 - w);
        _q1.copy(j.shoulderL.quaternion); j.shoulderL.rotation.set(-1.05, 0, 0.4); j.shoulderL.quaternion.slerp(_q1, 1 - w); // lead arm reaches
        _q1.copy(j.elbowL.quaternion); j.elbowL.rotation.set(-0.3, 0, 0); j.elbowL.quaternion.slerp(_q1, 1 - w);
      }
      if (dive > 0.3) {
        const w = smooth(clamp((dive - 0.3) / 0.5, 0, 1)); // superman reach
        _q1.copy(j.shoulderR.quaternion); j.shoulderR.rotation.set(-2.75, 0, -0.12); j.shoulderR.quaternion.slerp(_q1, 1 - w);
        _q1.copy(j.shoulderL.quaternion); j.shoulderL.rotation.set(-2.75, 0, 0.12); j.shoulderL.quaternion.slerp(_q1, 1 - w);
        _q1.copy(j.elbowR.quaternion); j.elbowR.rotation.set(-0.1, 0, 0); j.elbowR.quaternion.slerp(_q1, 1 - w);
        _q1.copy(j.elbowL.quaternion); j.elbowL.rotation.set(-0.1, 0, 0); j.elbowL.quaternion.slerp(_q1, 1 - w);
      }
      if (prone > 0.3) {
        // crawl: forearms planted by the head, reaching alternately with the crawl phase
        const w = smooth(clamp((prone - 0.3) / 0.5, 0, 1));
        this.ch.root.updateMatrixWorld(true);
        const reachR = 0.14 * cw * amt, reachL = -0.14 * cw * amt;
        j.shoulderR.getWorldPosition(_gT);
        _gT.x += fwdX * (0.32 + reachR) + sideX * 0.2; _gT.z += fwdZ * (0.32 + reachR) + sideZ * 0.2;
        _gT.y = this.groundY(_gT.x, _gT.z) + this.footH + 0.05;
        _armPole.set(fwdX * 0.6, -0.8, fwdZ * 0.6).normalize();
        _q1.copy(j.shoulderR.quaternion); _q2.copy(j.elbowR.quaternion);
        this.solveArm(j.shoulderR, j.elbowR, j.wristR, _gT, _armPole);
        j.shoulderR.quaternion.slerp(_q1, 1 - w); j.elbowR.quaternion.slerp(_q2, 1 - w);
        j.shoulderL.getWorldPosition(_gT);
        _gT.x += fwdX * (0.32 + reachL) - sideX * 0.2; _gT.z += fwdZ * (0.32 + reachL) - sideZ * 0.2;
        _gT.y = this.groundY(_gT.x, _gT.z) + this.footH + 0.05;
        _q1.copy(j.shoulderL.quaternion); _q2.copy(j.elbowL.quaternion);
        this.solveArm(j.shoulderL, j.elbowL, j.wristL, _gT, _armPole);
        j.shoulderL.quaternion.slerp(_q1, 1 - w); j.elbowL.quaternion.slerp(_q2, 1 - w);
      }
    }

    // ---- per-bone soft-body jiggle: each bone's "flesh" spring LAGS the freshly
    // posed target and overshoots, so limbs/chest jiggle when they accel/decel. ----
    const jAmt = this.ch.jiggle;
    if (jAmt > 0.001) {
      const JB_STIFF = 150, JB_DAMP = 13;
      for (let i = 0; i < this.jbones.length; i++) {
        const bone = this.jbones[i], sq = this.jsq[i], sv = this.jsv[i];
        _jt.copy(bone.quaternion); // the gait's clean pose this frame
        _jerr.copy(sq).invert().multiply(_jt); // rotation from the spring to the target (local)
        if (_jerr.w < 0) _jerr.set(-_jerr.x, -_jerr.y, -_jerr.z, -_jerr.w);
        _jev.set(_jerr.x, _jerr.y, _jerr.z).multiplyScalar(2); // ≈ angular error (rad)
        sv.addScaledVector(_jev, JB_STIFF * sdt).multiplyScalar(Math.exp(-JB_DAMP * sdt)); // exact, frame-rate-invariant decay
        _jdq.set(sv.x * sdt * 0.5, sv.y * sdt * 0.5, sv.z * sdt * 0.5, 1).normalize();
        sq.multiply(_jdq).normalize(); // advance the spring toward the target
        bone.quaternion.copy(_jt).slerp(sq, Math.min(jAmt, 1.5)); // blend pose → lagging spring
      }
    }

    // ---- gun hold: both arms IK onto the gun's grip points so the character
    // holds it two-handed. The barrel already points chest-forward = the cursor. ----
    if (ctrl.aiming) {
      this.ch.applyGunPose(sdt); // recoil shove / reload tilt / melee arc on the holder
      this.ch.root.updateMatrixWorld(true); // holder rides the (now-aimed) spine
      if (this.ch.gripPoints(_gDom, _gSup)) {
        const domR = this.ch.weaponDom === "right";
        _armPole.set(fwdX * 0.35, -1, fwdZ * 0.35).normalize(); // elbows drop down + a touch forward
        this.solveArm(domR ? j.shoulderR : j.shoulderL, domR ? j.elbowR : j.elbowL, domR ? j.wristR : j.wristL, _gDom, _armPole);
        // melee is one-handed — the support arm keeps its natural gait swing
        if (this.ch.twoHanded) {
          this.solveArm(domR ? j.shoulderL : j.shoulderR, domR ? j.elbowL : j.elbowR, domR ? j.wristL : j.wristR, _gSup, _armPole);
        }
      }
    }

    // ---- unarmed / hip-fire JAB: an armed swing rides the gun holder (applyGunPose),
    // but a bare-handed F-jab has no holder — punch the dominant arm forward so the
    // jab (and its impact FX) has a visible body instead of firing silently. ----
    if (this.ch.swing > 0.005 && !ctrl.aiming && this.ch.holdT < 0.01 && this.ch.throwT < 0.01) {
      this.ch.root.updateMatrixWorld(true);
      const domR = this.ch.weaponDom === "right";
      const sh = domR ? j.shoulderR : j.shoulderL, el = domR ? j.elbowR : j.elbowL, wr = domR ? j.wristR : j.wristL;
      const env = Math.sin((1 - this.ch.swing) * Math.PI); // 0→1→0 punch thrust
      sh.getWorldPosition(_gT);
      _gT.x += fwdX * 0.5 * env; _gT.z += fwdZ * 0.5 * env; _gT.y -= 0.04 * env; // forward, chest height
      _armPole.set(fwdX * 0.3, -1, fwdZ * 0.3).normalize();
      this.solveArm(sh, el, wr, _gT, _armPole);
    }

    // ---- grenade cook/throw: the dominant arm overrides everything else (a live
    // grenade in hand is the player-visible tell) — armed or bare-handed. ----
    if (this.ch.holdT > 0.01 || this.ch.throwT > 0.01) {
      this.ch.root.updateMatrixWorld(true);
      const domR = this.ch.weaponDom === "right";
      const sh = domR ? j.shoulderR : j.shoulderL, el = domR ? j.elbowR : j.elbowL, wr = domR ? j.wristR : j.wristL;
      const sSign = domR ? 1 : -1;
      sh.getWorldPosition(_gT);
      const hold = this.ch.holdT, thr = this.ch.throwT;
      // cook: hand raised beside/behind the head (pin pulled, arm loaded)
      _gT.x += (-fwdX * 0.15 + sideX * sSign * 0.12) * hold;
      _gT.z += (-fwdZ * 0.15 + sideZ * sSign * 0.12) * hold;
      _gT.y += 0.24 * hold;
      if (thr > 0.01) { // over-the-shoulder sweep to full forward extension
        const sweep = Math.sin((1 - thr) * Math.PI);
        _gT.x += fwdX * 0.55 * sweep;
        _gT.z += fwdZ * 0.55 * sweep;
        _gT.y += 0.18 * (1 - sweep) - 0.08 * sweep;
      }
      _armPole.set(fwdX * 0.2 + sideX * sSign * 0.5, -0.5, fwdZ * 0.2 + sideZ * sSign * 0.5).normalize();
      this.solveArm(sh, el, wr, _gT, _armPole);
    }
  }

  /** 3-D two-bone IK for an arm: place the wrist at `target`, elbow toward `pole`. */
  private solveArm(sh: THREE.Bone, el: THREE.Bone, wr: THREE.Bone, target: THREE.Vector3, pole: THREE.Vector3): void {
    const a = this.upperArm, b = this.lowerArm;
    sh.getWorldPosition(_H);
    _dir.subVectors(target, _H);
    const d = _dir.length();
    const dc = clamp(d, Math.abs(a - b) + 1e-3, a + b - 1e-3);
    if (d < 1e-5) _dir.set(0, -1, 0); else _dir.multiplyScalar(1 / d);
    const l = (a * a + dc * dc - b * b) / (2 * dc);
    const h = Math.sqrt(Math.max(0, a * a - l * l));
    _pp.copy(pole).addScaledVector(_dir, -pole.dot(_dir));
    if (_pp.lengthSq() < 1e-6) _pp.set(0, -1, 0);
    _pp.normalize();
    _K.copy(_H).addScaledVector(_dir, l).addScaledVector(_pp, h);
    _thigh.subVectors(_K, _H).normalize();
    _hinge.crossVectors(_pp, _thigh).normalize();
    orientBone(sh, _thigh, _hinge);
    sh.updateWorldMatrix(true, false);
    _calf.subVectors(target, _K).normalize();
    orientBone(el, _calf, _hinge);
    el.updateWorldMatrix(true, false);
    void wr; // wrist keeps its inherited orientation (hand rests on the gun)
  }

  /** Anatomical limits so no joint bends into an impossible pose. Knees/elbows
   *  fold one way only; wrists/ankles/spine/neck get sane cones. Hips & shoulders
   *  are deliberately excluded. */
  private clampJoints(): void {
    const j = this.ch.joints;
    // Knees & ankles are driven by the IK / foot-leveling (orientBone). Their
    // quaternion→euler decomposition carries a 180° artifact, so clamping the
    // euler CORRUPTS the orientation (it was tipping the feet onto their toes).
    // They're already anatomically sane from the solver — leave them untouched.
    j.elbowR.rotation.x = clamp(j.elbowR.rotation.x, -2.7, 0.15); // fold forward only
    j.elbowL.rotation.x = clamp(j.elbowL.rotation.x, -2.7, 0.15);
    j.wristR.rotation.x = clamp(j.wristR.rotation.x, -0.7, 0.7);
    j.wristL.rotation.x = clamp(j.wristL.rotation.x, -0.7, 0.7);
    const sp = j.spine.rotation;
    sp.set(clamp(sp.x, -0.6, 0.9), clamp(sp.y, -0.9, 0.9), clamp(sp.z, -0.6, 0.6));
    const nk = j.neck.rotation;
    nk.set(clamp(nk.x, -0.7, 0.8), clamp(nk.y, -1.35, 1.35), clamp(nk.z, -0.5, 0.5));
  }

  /** Nearest walkable surface under a foot (probe down from the TRUE ground height, so a
   *  stair-smoothed render body dipped below the tread can't sink the probe under the step). */
  private groundY(x: number, z: number): number {
    const g = this.world.groundBelow(x, z, this.probeBaseY + this.ch.height * STEP_FRAC);
    return g !== null ? g : this.probeBaseY;
  }

  /** 3-D gap between a foot and where it ought to be (under the hip, on the
   *  ground) — the Y term re-plants feet after landing on a new surface height. */
  private offIdeal(i: number, pos: THREE.Vector3, sideX: number, sideZ: number): number {
    const s = i === 0 ? 1 : -1;
    const ix = pos.x + sideX * s * this.stanceHalf, iz = pos.z + sideZ * s * this.stanceHalf;
    const iy = this.groundY(ix, iz) + this.footH;
    return Math.hypot(this.foot[i].x - ix, this.foot[i].y - iy, this.foot[i].z - iz);
  }

  /** Capture point: predict where the CoM will be when the foot PLANTS (a step
   *  from now), then straddle it by a small (capped) amount so the foot lands
   *  under the moving body — no chronic lag — without a big forward reach. */
  private retarget(i: number, pos: THREE.Vector3, vx: number, vz: number, sideX: number, sideZ: number, stepDur: number): void {
    const s = i === 0 ? 1 : -1;
    const px = pos.x + vx * stepDur; // CoM at plant time (tracks the body)
    const pz = pos.z + vz * stepDur;
    // braking: plant a full stride AHEAD of the decelerating CoM (a braced strut)
    let hx = vx * stepDur * (0.5 + 0.45 * this.brake), hz = vz * stepDur * (0.5 + 0.45 * this.brake);
    const cap = clamp(0.15 + Math.hypot(vx, vz) * 0.042, 0.15, 0.62) * (1 + 0.5 * this.brake);
    const hm = Math.hypot(hx, hz);
    if (hm > cap) { const f = cap / hm; hx *= f; hz *= f; }
    let tx = px + hx + sideX * s * this.stanceHalf;
    let tz = pz + hz + sideZ * s * this.stanceHalf;
    // anti-cross: a fast turn rotates the `side` axis faster than the planted foot
    // moves, so the capture target can fall on the FAR side of the planted foot →
    // crossed legs. Keep foot i at least a stance-width to its OWN side of the other
    // (planted) foot. `sep` = rightward offset of the target from the planted foot.
    // Push is CAPPED: a 180° reversal could otherwise demand a ~1 m goose-step lunge;
    // the residual un-crosses on the next (turn-shortened) step.
    const o = 1 - i;
    const sep = (tx - this.plant[o].x) * sideX + (tz - this.plant[o].z) * sideZ;
    const minSep = this.stanceHalf;
    if (s > 0 ? sep < minSep : sep > -minSep) {
      const d = clamp(s * minSep - sep, -this.stanceHalf * 2, this.stanceHalf * 2);
      tx += sideX * d; tz += sideZ * d;
    }
    this.swingTo[i].set(tx, this.groundY(tx, tz) + this.footH, tz);
  }

  /** 3-D two-bone IK: ankle at `target`, knee toward the pole (forward).
   *  footYaw = the foot's OWN heading (planted toes lock; no grinding on turns).
   *  anklePitch tilts the sole (heel-strike/toe-off roll + slope); positive = toe
   *  down, which also RAISES the ankle over the toe (heel rise) via a scratch copy
   *  of the target — plant state itself is never mutated (it's the no-slip truth). */
  private solveLeg(
    hip: THREE.Bone, knee: THREE.Bone, ankle: THREE.Bone, target: THREE.Vector3,
    poleX: number, poleZ: number, splaySign: number, air: number, airHipX: number, airKneeX: number,
    drive = 0, driveHipX = DRIVE_HIP, driveKneeX = DRIVE_KNEE, footYaw = 0, anklePitch = 0,
  ): void {
    const ffx = Math.sin(footYaw), ffz = Math.cos(footYaw);
    _t8.copy(target);
    if (anklePitch > 0) { // heel rise: the ankle pivots up and forward over the toe
      const hr = Math.sin(anklePitch);
      _t8.y += this.footD * hr;
      _t8.x += ffx * this.footD * 0.4 * hr;
      _t8.z += ffz * this.footD * 0.4 * hr;
    }
    hip.getWorldPosition(_H).divideScalar(this.worldScale); // world -> root-local rig units (see worldScale)
    _dir.subVectors(_t8, _H);
    let d = _dir.length();
    const a = this.thigh, b = this.calf;
    const dc = clamp(d, Math.abs(a - b) + 1e-3, a + b - 1e-3);
    if (d < 1e-5) { _dir.set(0, -1, 0); } else _dir.multiplyScalar(1 / d);
    const l = (a * a + dc * dc - b * b) / (2 * dc);
    const h = Math.sqrt(Math.max(0, a * a - l * l));
    _pole.set(poleX, 0, poleZ);
    _pp.copy(_pole).addScaledVector(_dir, -_pole.dot(_dir));
    if (_pp.lengthSq() < 1e-6) _pp.set(0, 0, 1);
    _pp.normalize();
    _K.copy(_H).addScaledVector(_dir, l).addScaledVector(_pp, h);
    _thigh.subVectors(_K, _H).normalize();
    _hinge.crossVectors(_pp, _thigh).normalize();
    orientBone(hip, _thigh, _hinge);
    hip.rotateZ(splaySign * HIP_SPLAY * (1 - air));
    hip.updateWorldMatrix(true, false);
    _calf.subVectors(_t8, _K).normalize();
    orientBone(knee, _calf, _hinge);

    if (air > 0.01) {
      // slerp the WHOLE local orientation toward a clean sagittal air pose (body
      // frame) so it rotates with the character and doesn't inherit the IK's
      // world-space twist — that leak is what contorted the legs on bhop+turn.
      _qAir.setFromEuler(_eAir.set(airHipX, 0, 0));
      hip.quaternion.slerp(_qAir, air);
      hip.updateWorldMatrix(true, false);
      _qAir.setFromEuler(_eAir.set(airKneeX, 0, 0));
      knee.quaternion.slerp(_qAir, air);
    }
    if (drive > 0.01) {
      // grounded sprint drive: same clean body-frame slerp as the air pose (no
      // world-space twist leak). driveHipX/KneeX sweep back→high-knee→reach.
      _qAir.setFromEuler(_eAir.set(driveHipX, 0, 0));
      hip.quaternion.slerp(_qAir, drive);
      hip.updateWorldMatrix(true, false);
      _qAir.setFromEuler(_eAir.set(driveKneeX, 0, 0));
      knee.quaternion.slerp(_qAir, drive);
    }
    knee.updateWorldMatrix(true, false);
    // orient the foot: sole down tilted by anklePitch about the foot's OWN heading
    // (footYaw — planted toes keep their plant heading, no grinding), toe forward.
    // The foot's local +X must be the char's LEFT (= cross(up, forward)) because
    // limb meshes aren't mirrored — their local +X is world +X = the char's left.
    // down tilts BACKWARD for positive pitch → toe drops (toe-off); negative → heel-strike.
    const st = Math.sin(anklePitch), ct = Math.cos(anklePitch);
    _down8.set(-ffx * st, -ct, -ffz * st);
    orientBone(ankle, _down8, _hinge.set(ffz, 0, -ffx));
    // airborne the foot follows the shin into the tuck instead of hovering level
    if (air > 0.01) ankle.quaternion.slerp(_qId, air * 0.8);
  }
}

// ---- scratch + helpers ------------------------------------------------------
const _ws = new THREE.Vector3(); // root world-scale probe (see Gait.worldScale)
const _H = new THREE.Vector3(), _dir = new THREE.Vector3(), _pole = new THREE.Vector3();
const _pp = new THREE.Vector3(), _K = new THREE.Vector3(), _thigh = new THREE.Vector3();
const _calf = new THREE.Vector3(), _hinge = new THREE.Vector3(), _mid = new THREE.Vector3();
const _bx = new THREE.Vector3(), _by = new THREE.Vector3(), _bz = new THREE.Vector3();
const _m = new THREE.Matrix4(), _qw = new THREE.Quaternion(), _qp = new THREE.Quaternion();
const _qAir = new THREE.Quaternion(), _eAir = new THREE.Euler();
const _t8 = new THREE.Vector3(), _down8 = new THREE.Vector3(), _qId = new THREE.Quaternion();
const _jt = new THREE.Quaternion(), _jerr = new THREE.Quaternion(), _jdq = new THREE.Quaternion(), _jev = new THREE.Vector3();
const _gDom = new THREE.Vector3(), _gSup = new THREE.Vector3(), _armPole = new THREE.Vector3(), _gT = new THREE.Vector3();
const _q1 = new THREE.Quaternion(), _q2 = new THREE.Quaternion(); // stance-pose blend scratch

/** Orient a bone so its local -Y points along `down` and local +X along `hinge`. */
function orientBone(bone: THREE.Bone, down: THREE.Vector3, hinge: THREE.Vector3): void {
  _by.copy(down).multiplyScalar(-1).normalize();
  _bx.copy(hinge).normalize();
  _bz.crossVectors(_bx, _by).normalize();
  _bx.crossVectors(_by, _bz).normalize();
  _m.makeBasis(_bx, _by, _bz);
  _qw.setFromRotationMatrix(_m);
  (bone.parent as THREE.Object3D).getWorldQuaternion(_qp);
  bone.quaternion.copy(_qp.invert().multiply(_qw));
}

function smooth(t: number): number { return t * t * (3 - 2 * t); }
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }
function clamp(x: number, lo: number, hi: number): number { return x < lo ? lo : x > hi ? hi : x; }
function shortestAngle(from: number, to: number): number { return Math.atan2(Math.sin(to - from), Math.cos(to - from)); }
function wobble(x: number): number { return (Math.sin(x) + 0.5 * Math.sin(x * 2.17 + 1.3) + 0.3 * Math.sin(x * 4.11 + 2.7)) / 1.8; }
